import React, {useEffect, useMemo, useRef} from 'react';
import {Dropdown} from "@/components/dropdown/Dropdown";
import {DropdownItem} from "@/components/dropdown/DropdownItem";
import {projectApiActions} from "@/store/features/project/projectApiSlice";
import {usersApiActions} from "@/store/features/user/userApiSlice";
import * as MdIcons from 'react-icons/md';
import {MdKeyboardArrowDown} from 'react-icons/md';
import {useToast} from '@/app/components/toast/toast';
import type {IconType} from 'react-icons';

interface Agent {
    name: string;
    description: string;
    displayName: string;
    icon: string;
}

interface AgentDropdownProps {
    selectedAgent: string;
    onAgentSelect: (agent: string) => void;
    direction?: 'up' | 'down';
    initialAgent?: string | null;
    isNewChat?: boolean;
}

const FALLBACK_AGENT = 'generic';

export const AgentDropdown: React.FC<AgentDropdownProps> = ({
                                                                selectedAgent,
                                                                onAgentSelect,
                                                                direction = 'up',
                                                                initialAgent,
                                                                isNewChat = false,
                                                            }) => {
    const {toast} = useToast();
    const projectId = "01JFGEEM0KKJ0N40CJWKHT37ZA";
    const lastPickWasAuto = useRef(true);
    const seededOnceRef = useRef(false);
    const {
        data: agents,
        isLoading,
        isError: agentsError,
        refetch: refetchAgents
    } = projectApiActions.useGetProjectAgentQuery({projectId});
    const {
        data: defaultData,
        isError: defaultError,
    } = usersApiActions.useGetDefaultAgentQuery();
    const {
        data: favoritesData,
        isLoading: loadingFavorites,
        isError: favoritesError,
    } = usersApiActions.useGetFavoritesQuery();

    const sortedAgents = useMemo<Agent[]>(() => {
        if (!agents || agents.length === 0) return [];

        const def = defaultData?.defaultAgentId ?? null;
        const favSet = new Set(favoritesData?.favorites ?? []);

        const score = (a: Agent) => {
            if (def && a.name === def) return 0;
            if (favSet.has(a.name)) return 1;
            return 2;
        };

        return [...agents].sort((a, b) => {
            const s = score(a) - score(b);
            return s !== 0 ? s : a.displayName.localeCompare(b.displayName);
        });
    }, [agents, defaultData, favoritesData]);

    const initialAgentValid = useMemo(
        () => !!initialAgent && !!agents?.some(a => a.name === initialAgent),
        [initialAgent, agents]
    )

    const effectiveSelectedName =
        selectedAgent ||
        (initialAgentValid ? (initialAgent as string) : (sortedAgents[0]?.name ?? ''));

    useEffect(() => {
        if (!agents || agents.length === 0) return;
        if (selectedAgent) return;
        if (seededOnceRef.current) return;

        const initialValid = !!initialAgent && agents.some(a => a.name === initialAgent);
        if (initialValid) {
            seededOnceRef.current = true;
            lastPickWasAuto.current = true;
            onAgentSelect(initialAgent!);
            return;
        }

        const def = defaultData?.defaultAgentId ?? null;
        const hasDef = !!def && agents.some(a => a.name === def);
        const hasFallback = agents.some(a => a.name === FALLBACK_AGENT);

        let pick: string | undefined;
        if (hasDef) pick = def!;
        else if (hasFallback) pick = FALLBACK_AGENT;
        else pick = sortedAgents[0]?.name;

        if (pick) {
            lastPickWasAuto.current = true;
            seededOnceRef.current = true;
            onAgentSelect(pick);
        }
    }, [agents, sortedAgents, defaultData, initialAgent, selectedAgent, onAgentSelect]);


    useEffect(() => {
        if (!agents || agents.length === 0) return;
        if (!isNewChat) return;
        if (initialAgent) return;
        const def = defaultData?.defaultAgentId ?? null;
        if (!def) return;
        if (!agents.some(a => a.name === def)) return;
        if (!lastPickWasAuto.current) return;
        if (selectedAgent !== FALLBACK_AGENT) return;
        if (selectedAgent === def) return;
        onAgentSelect(def);
    }, [agents, defaultData, initialAgent, selectedAgent, onAgentSelect, isNewChat]);

    useEffect(() => {
        if (agentsError) {
            toast('Failed to load project agents. Please try again.');
        }
    }, [agentsError, toast]);
    useEffect(() => {
        if (defaultError) {
            toast('Default agent info is temporarily unavailable.');
        }
    }, [defaultError, toast]);
    useEffect(() => {
        if (favoritesError) {
            toast('Favorite agents are temporarily unavailable.');
        }
    }, [favoritesError, toast]);

    const selectedAgentObj = agents?.find(agent => agent.name === effectiveSelectedName);
    const displayName = selectedAgentObj
        ? selectedAgentObj.displayName
        : sortedAgents.length > 0
            ? sortedAgents[0].displayName
            : 'No agents';

    const resolveIcon = (iconName: string): IconType => {
        return (MdIcons as Record<string, IconType>)[iconName] || MdIcons.MdOutlineSmartToy;
    };

    const SelectedIcon = selectedAgentObj
        ? resolveIcon(selectedAgentObj.icon)
        : MdIcons.MdOutlineSmartToy;

    if (isLoading || loadingFavorites) {
        return (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 " aria-busy="true">
                <span className="text-sm text-gray-500">Loading agents...</span>
            </div>
        );
    }

    if (agentsError) {
        return (
            <div className="flex items-center gap-3 px-3 py-1.5 rounded-full bg-gray-100">
                <span className="text-sm text-red-600">Failed to load agents</span>
                <button
                    type="button"
                    onClick={() => refetchAgents()}
                    className="text-sm px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                    aria-label="Retry loading agents"
                >
                    Retry
                </button>
            </div>
        );
    }

    if (!sortedAgents.length) {
        return (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100">
                <MdIcons.MdOutlineSmartToy size={16} className="text-gray-700"/>
                <span className="text-sm text-gray-700">No agents available</span>
            </div>
        );
    }

    return (
        <Dropdown
            menu={
                <div className="max-h-75 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-transparent">
                    {sortedAgents.map(agent => {
                        const IconComponent = resolveIcon(agent.icon);
                        return (
                            <div
                                key={agent.name}
                                onClick={() => {
                                    lastPickWasAuto.current = false;
                                    onAgentSelect(agent.name);
                                }}
                            >
                                <DropdownItem>
                                    <span className="flex items-center gap-2">
                                        <IconComponent size={16}/>
                                        <span className="text-sm text-gray-700">{agent.displayName}</span>
                                    </span>
                                </DropdownItem>
                            </div>
                        );
                    })}
                </div>
            }
            direction={direction}
            className="relative min-w-48 z-50"
        >
            <div
                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors duration-200">
                <SelectedIcon size={16} color="black"/>
                <span className="text-sm text-gray-700">{displayName}</span>
                <MdKeyboardArrowDown size={16} className="text-gray-700"/>
            </div>
        </Dropdown>
    );
};
